import { Grid, Typography } from "@mui/material";
import {
  AccessTime as AccessTimeIcon,
  Edit as EditIcon,
  Person as PersonIcon,
} from "@mui/icons-material";
import { Link as RouterLink } from "react-router-dom";
import { FormatDate } from "~/lib/date";
import { Flex } from "~/components/Flex";
import {
  RiskFactorsIcons,
  RiskFactorsIconsProps,
} from "~/pages/space/security/components/RiskFactors/RiskFactorsIcons";
import { ReactNode } from "react";
import { Author, AuthorType } from "~/operations";
import { Space } from "~/lib/types";

export type HeaderProps = {
  id: string;
  icon?: ReactNode;
  title: string;
  space?: Space;
  created?: string | null;
  createdBy?: Author | null;
  lastModified?: string | null;
  riskFactors?: RiskFactorsIconsProps["riskFactors"];
  rightSection?: ReactNode;
  tags?: ReactNode;
};

export const Header = ({
  id,
  icon,
  title,
  space,
  lastModified,
  created,
  createdBy,
  riskFactors,
  rightSection,
  tags,
}: HeaderProps) => {
  return (
    <Grid id={id} container spacing={2} sx={{ mb: 3, flexWrap: "nowrap" }}>
      <Grid
        sx={{ display: "flex", flexDirection: "column" }}
        size={{
          xs: 12,
          sm: 10,
        }}
      >
        <Flex alignItems="center" gap={2}>
          {icon}
          <Typography
            id="title"
            variant="h4"
            component="h1"
            fontWeight={700}
            textTransform="uppercase"
          >
            {title}{" "}
            <RiskFactorsIcons
              riskFactors={riskFactors}
              shouldLimit={false}
              showBackgroundColor
            />
          </Typography>
        </Flex>
        <Flex mt={icon ? "12px" : 0}>
          {createdBy?.name && (
            <Typography
              id="created-by"
              {...(createdBy.type === AuthorType.ServiceAccount && {
                component: RouterLink,
                to: `/space/service-accounts?spaceId=${space?.id}&queryTerms=${createdBy.name}`,
              })}
              variant="caption"
              color="text.secondary"
              fontWeight={700}
              sx={{ mr: 3, display: "inline-flex", alignItems: "center" }}
            >
              <PersonIcon fontSize="inherit" sx={{ mr: 0.5 }} />
              Created by {createdBy.name}
            </Typography>
          )}
          {created && (
            <Typography
              id="published-at"
              variant="caption"
              color="text.secondary"
              fontWeight={700}
              sx={{ mr: 3, display: "inline-flex", alignItems: "center" }}
            >
              <AccessTimeIcon fontSize="inherit" sx={{ mr: 0.5 }} />
              Created {FormatDate(created)}
            </Typography>
          )}
          {lastModified && (
            <Typography
              id="modified-at"
              variant="caption"
              color="text.secondary"
              fontWeight={700}
              sx={{ display: "inline-flex", alignItems: "center" }}
            >
              <EditIcon fontSize="inherit" sx={{ mr: 0.5 }} />
              Last modified {FormatDate(lastModified)}
            </Typography>
          )}
          {tags}
        </Flex>
      </Grid>
      <Flex
        sx={{
          flex: "1 0 auto",
          alignSelf: "center",
          justifyContent: "flex-end",
        }}
      >
        {rightSection}
      </Flex>
    </Grid>
  );
};
